import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Printer, Download, Share2, ShieldCheck, CheckCircle2, Activity, Award } from "lucide-react";

export default function ReportViewerModal({ report, isOpen, onClose }) {
  if (!isOpen || !report) return null;

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = async () => {
    try {
      const t = localStorage.getItem("sigmacare_token");
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reports/${report.id}/file?download=1`, { withCredentials: true, responseType: "blob", headers: t ? { Authorization: `Bearer ${t}` } : {} });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `${report.test_name || "report"}.pdf`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.message("No signed PDF is attached to this report yet — use Print to save this page as PDF.");
      window.print();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/70 backdrop-blur-sm animate-in fade-in">
      <div 
        data-testid="report-viewer-modal"
        className="w-full max-w-3xl bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Modal Action Bar (No Print) */}
        <div className="no-print bg-[#0D3B2E] text-white p-4 flex items-center justify-between border-b border-emerald-800">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-amber-300" />
            <span className="font-serif-luxury text-lg font-bold">Secure Digital Medical Report</span>
            <span className="text-[10px] bg-emerald-700 text-emerald-200 px-2 py-0.5 rounded font-mono uppercase">
              {report.status || "Approved"}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              data-testid="report-print-btn"
              className="p-2 bg-emerald-800 hover:bg-emerald-700 text-white rounded-xl text-xs flex items-center gap-1.5 transition-colors"
              title="Print Official Report"
            >
              <Printer className="w-4 h-4" />
              <span className="hidden sm:inline">Print</span>
            </button>
            <button
              onClick={handleDownload}
              data-testid="report-download-btn"
              className="p-2 bg-amber-500 hover:bg-amber-600 text-[#07221A] rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors"
              title="Download PDF"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Download PDF</span>
            </button>
            <button
              onClick={onClose}
              data-testid="report-close-btn"
              className="p-2 text-gray-300 hover:text-white rounded-xl hover:bg-emerald-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Printable Official Clinical Report Document */}
        <div className="p-6 sm:p-8 overflow-y-auto bg-white print-only-card space-y-6">
          
          {/* Official Letterhead */}
          <div className="flex flex-col sm:flex-row justify-between items-start border-b-2 border-[#0D3B2E] pb-4 gap-4">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#0D3B2E] text-amber-300 flex items-center justify-center font-bold text-sm">
                  <Activity className="w-5 h-5" />
                </div>
                <h2 className="font-serif-luxury text-2xl font-bold text-[#0D3B2E]">
                  6SigmaCare Polyclinic & Diagnostics
                </h2>
              </div>
              <p className="text-[11px] text-gray-600 mt-1">
                4,000 Sq. Ft. Integrated Healthcare Centre • Salt Lake Sector V & Ballygunge, Kolkata
              </p>
              <p className="text-[10px] text-gray-500">
                NABL Accredited & AERB Approved Clinical Diagnostic Laboratory • +91 98300 12345
              </p>
            </div>

            <div className="text-right text-xs">
              <span className="px-2.5 py-1 bg-emerald-50 text-emerald-800 font-mono font-bold border border-emerald-300 rounded">
                REPORT ID: {report.id}
              </span>
              <p className="text-[11px] text-gray-500 mt-1">Booking Ref: {report.booking_id}</p>
              <p className="text-[11px] text-gray-500">Date of Exam: <b>{report.performed_date}</b></p>
            </div>
          </div>

          {/* Patient Header Box */}
          <div className="bg-[#FBF9F4] p-4 rounded-2xl border border-gray-200 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <span className="text-[10px] uppercase text-gray-400 font-bold">Patient Name</span>
              <p className="font-bold text-gray-900">{report.patient_name}</p>
            </div>
            <div>
              <span className="text-[10px] uppercase text-gray-400 font-bold">Category</span>
              <p className="font-semibold text-[#0D3B2E]">{report.category}</p>
            </div>
            <div>
              <span className="text-[10px] uppercase text-gray-400 font-bold">Investigation</span>
              <p className="font-bold text-gray-900">{report.test_name}</p>
            </div>
            <div>
              <span className="text-[10px] uppercase text-gray-400 font-bold">Consultant</span>
              <p className="font-semibold text-gray-800">{report.doctor_or_pathologist}</p>
            </div>
          </div>

          {/* Test Parameters & Values Table */}
          {report.findings && report.findings.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#0D3B2E] border-b border-gray-200 pb-1">
                Quantitative & Qualitative Findings
              </h4>
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-left text-xs">
                  <thead className="bg-[#0D3B2E] text-white text-[11px]">
                    <tr>
                      <th className="p-2.5">Test Parameter</th>
                      <th className="p-2.5">Observed Value</th>
                      <th className="p-2.5">Units</th>
                      <th className="p-2.5">Biological Reference Range</th>
                      <th className="p-2.5 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {report.findings.map((f, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="p-2.5 font-semibold text-gray-800">{f.parameter}</td>
                        <td className="p-2.5 font-bold text-gray-900">{f.value}</td>
                        <td className="p-2.5 text-gray-500">{f.unit || "—"}</td>
                        <td className="p-2.5 text-gray-600 font-mono text-[11px]">{f.normal_range || "—"}</td>
                        <td className="p-2.5 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            f.status === "Normal" 
                              ? "bg-emerald-100 text-emerald-800" 
                              : "bg-amber-100 text-amber-800"
                          }`}>
                            {f.status || "Normal"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Overall Interpretation & Doctor Remarks */}
          <div className="p-4 bg-emerald-50/70 rounded-2xl border border-emerald-200 space-y-1.5 text-xs">
            <h4 className="font-bold text-[#0D3B2E] flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-[#3B8271]" /> Consultant Clinical Impression & Interpretation:
            </h4>
            <p className="text-gray-800 leading-relaxed font-medium">
              {report.overall_interpretation}
            </p>
          </div>

          {/* Digital Signatures Footer */}
          <div className="pt-6 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-end gap-4 text-xs">
            <div className="text-[10px] text-gray-500 space-y-0.5">
              <p>• Verified with Automated VITEK / Mindray BC-6800 / GE Healthcare Ultrasound System</p>
              <p>• QR Code Encrypted Digital Health Signature compliant with DPDP & NABH guidelines.</p>
              <p>• Generated on: {new Date().toLocaleDateString("en-IN")}</p>
            </div>

            <div className="text-right">
              <div className="font-serif-luxury italic text-sm text-[#0D3B2E] font-bold mb-1">
                {report.signed_by || "Dr. Tanmoy Ghosh, MD"}
              </div>
              <p className="text-[10px] text-gray-600 font-semibold uppercase tracking-wider">
                Authorized Signatory & Pathologist
              </p>
              <p className="text-[9px] text-gray-400">WBMC Registration No. WBMC-48192</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
